import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef } from 'react';
import MainWrapper from '../wrappers/mainWrapper';
import CollapsedMatchBlock from '../restructured/CollapsedMatchBlock';
import ExpandedMatchBlock from '../restructured/ExpandedMatchBlock';
import { useParams } from 'react-router-dom';
import '../../css/restructuredTransitions.css';
import PDFViewer from '../restructured/PDFViewer';
import Filters from '../restructured/Filters';
import ZoneFilter from '../restructured/ZoneFilter';
import GoIcon from '../../img/_Go.png';
import yieldIcon from '../../img/_Yield.png';
import PDFExport from '../restructured/assets/export-icon.svg';
import NukeButton from '../restructured/NukeButton';
import revertMatch from '../restructured/functions/revertMatch';
import toggleExpanded from '../restructured/functions/toggleExpanded';
import { fetchData } from '../restructured/functions/fetchData';
import saveMatchResults from '../restructured/functions/saveMatchResults';
import saveMatchNotes from '../restructured/functions/saveMatchNotes';
import saveMarkForExport from '../restructured/functions/saveMarkForExport';
import { revertAllMatches } from '../restructured/functions/revertAllMatches';
import { saveLock } from '../restructured/functions/saveLock';
import collapseIcon from '../restructured/assets/collapse-icon.png';
import expandIcon from '../restructured/assets/expand-icon.png';
import { useSelector } from 'react-redux';
import pdfIcon from '../restructured/assets/pdf-light.png';

const RestructuredResults = () => {
  const { comparisonID } = useParams();
  const userData = useSelector(state => state.user)
  const [data, setData] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState('loading-spinner');
  const [visibleDocs, setVisibleDocs] = useState({ doc1: false, doc2: false });
  const [designationFilter, setDesignationFilter] = useState('All');
  const [zoneFilter, setZoneFilter] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectAllForExport, setSelectAllForExport] = useState(false);
  const [selectAllExpanded, setSelectAllExpanded] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingExpand, setLoadingExpand] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const pageSize = 25;
  const [currentPage, setCurrentPage] = useState(1);
  const scrollContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const paginationRef = useRef(null);
  const [pairingsTopOffset, setPairingsTopOffset] = useState(40);
  const isAprilFools = useMemo(() => {
    const now = new Date();
    return now.getMonth() === 3 && now.getDate() === 1;
  }, []);
  const shouldRunAprilFools = useMemo(() => {
    const email = String(userData?.email || '').toLowerCase();
    if (!isAprilFools || !email) return false;
    const allowed = ['elkmont', 'diem', 'kralik', 'getlevlr', 'pdgcreative.com']
      .some(token => email.includes(token));
    if (!allowed) return false;
    const envDisabled = ['1', 'true', 'yes', 'on'].includes(
      String(process.env.REACT_APP_DISABLE_APRIL_FOOLS_PRANK || '').toLowerCase()
    );
    if (envDisabled) return false;
    if (typeof window !== 'undefined') {
      try {
        const storageFlag = String(localStorage.getItem('levlr:disable_april_fools_prank') || '').toLowerCase();
        if (['1', 'true', 'yes', 'on'].includes(storageFlag)) return false;
      } catch (e) {
        // ignore storage read errors
      }
      const params = new URLSearchParams(window.location.search || '');
      const noPrank = String(params.get('no_prank') || params.get('prank') || '').toLowerCase();
      if (['1', 'true', 'off', 'disable', 'disabled'].includes(noPrank)) return false;
    }
    return true;
  }, [isAprilFools, userData?.email]);
  const [aprilFoolsStage, setAprilFoolsStage] = useState('done');
  const [aprilFoolsProgress, setAprilFoolsProgress] = useState(0);
  const [aprilFoolsOutput, setAprilFoolsOutput] = useState([]);
  const aprilFoolsOutputRef = useRef(null);
  const aprilFoolsLines = useMemo(() => ([
    '[SYS] sudo auth --user=system --scope=database',
    '[SYS] Preparing destructive action against: claims_production',
    '[SQL] DROP DATABASE claims_production;',
    '[FS] rm -rf /srv/levlr/data/claims/*',
    '[FS] rm -rf /srv/levlr/data/line_items/*',
    '[QUEUE] Purging pending jobs...',
    '[CACHE] Flushing redis keys: claim:*',
    '[LOG] Archiving evidence trail...',
    '[SEC] Revoking active client sessions...',
    '[SYS] Running final irreversible cleanup...'
  ]), []);

  useEffect(() => {
    fetchData(comparisonID, setData, setLoadingStatus);
  }, [comparisonID]);

  useEffect(() => {
    if (!shouldRunAprilFools) {
      setAprilFoolsStage('done');
      return;
    }
    setAprilFoolsStage((prev) => (prev === 'done' ? 'prompt' : prev));
  }, [shouldRunAprilFools]);

  useEffect(() => {
    if (!shouldRunAprilFools || aprilFoolsStage !== 'deleting') return;
    let progressValue = 0;
    let lineIndex = 0;
    const interval = setInterval(() => {
      progressValue = Math.min(100, progressValue + (Math.floor(Math.random() * 9) + 4));
      setAprilFoolsProgress(progressValue);
      if (lineIndex < aprilFoolsLines.length) {
        setAprilFoolsOutput((prev) => [...prev, aprilFoolsLines[lineIndex]]);
        lineIndex += 1;
      }
      if (progressValue >= 100) {
        clearInterval(interval);
        setAprilFoolsStage('fetching');
      }
    }, 260);
    return () => clearInterval(interval);
  }, [aprilFoolsLines, aprilFoolsStage, shouldRunAprilFools]);

  useEffect(() => {
    if (!aprilFoolsOutputRef.current) return;
    aprilFoolsOutputRef.current.scrollTop = aprilFoolsOutputRef.current.scrollHeight;
  }, [aprilFoolsOutput]);

  useEffect(() => {
    if (!userData?._id || !comparisonID) return;
    const fetchSignedUrl = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_GET_PDFS}/${userData._id}/${comparisonID}.pdf`
        );
        if (!res.ok) {
          setSignedUrl(null);
          return;
        }
        const awsdata = await res.json();
        if (awsdata?.url) {
          setSignedUrl(awsdata.url);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch signed PDF URL:', err);
        setSignedUrl(null);
      }
    };  
    fetchSignedUrl();
    const intervalId = setInterval(fetchSignedUrl, 10000);
    return () => clearInterval(intervalId);
  }, [userData?._id, comparisonID]);

  const saveResults = useCallback(async (resultsID, updatedResults) => {
    if (!data) return;
    const fullUpdatedData = { ...data, results: updatedResults };
    try {
      const response = await fetch(`${process.env.REACT_APP_UPDATE_RESULTS}/${resultsID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: fullUpdatedData }),
      });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'Failed to save results');
      }
    } catch (err) {
      console.error('❌ Error saving results:', err);
    }
  }, [data]);

  const lineItemMap = useMemo(() => {
    if (!data?.cleanParsedEstData?.lineItems) return {};
    const map = {};
    for (const li of data.cleanParsedEstData.lineItems) {
      const id = Object.keys(li)[0];
      map[id] = li[id];
    }
    return map;
  }, [data?.cleanParsedEstData?.lineItems]);

  const usedDoc2Ids = useMemo(() => {
    const ids = new Set();
    data?.results?.forEach(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      result.Matches[0][matchKey]["User Modified"]?.doc2?.ids?.forEach(id => ids.add(id));
    });
    return ids;
  }, [data?.results]);

  const unusedInsLineItems = useMemo(() => {
    return data?.cleanParsedInsData?.lineItems?.filter(item => {
      const id = Object.keys(item)[0];
      return !usedDoc2Ids.has(id);
    });
  }, [data?.cleanParsedInsData, usedDoc2Ids]);

  const doc1Zones = useMemo(() => data?.cleanParsedEstData?.userModifiedZones
    ? Object.keys(data.cleanParsedEstData.userModifiedZones)
    : [], [data?.cleanParsedEstData]);

  const lineItemUsageMap = useMemo(() => {
    const usageMap = {};
    data?.results?.forEach(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      const userModified = result.Matches[0][matchKey]?.["User Modified"];
      if (!userModified) return;
      [...(userModified.doc1?.ids || []), ...(userModified.doc2?.ids || [])]
        .forEach(id => { usageMap[id] = (usageMap[id] || 0) + 1; });
    });
    return usageMap;
  }, [data?.results]);

  const filteredResults = useMemo(() => {
    if (!data?.results) return [];
    if (designationFilter === "All" && zoneFilter.length === 0) return data.results;
    return data.results.filter(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      const match = result.Matches[0][matchKey];

      const designation = match?.["User Modified"]?.Designation || "No-Match";
      if (designationFilter !== "All" && designation !== designationFilter) return false;

      if (zoneFilter.length > 0) {
        const doc1Ids = match?.["User Modified"]?.doc1?.ids || [];
        const zonesForIds = doc1Ids.map(id => lineItemMap[id]?.userModifiedZone).filter(Boolean);
        if (!zonesForIds.some(z => zoneFilter.includes(z))) return false;
      }

      return true;
    });
  }, [data?.results, designationFilter, zoneFilter, lineItemMap]);

  useEffect(() => {
    setCurrentPage(1);
  }, [designationFilter, zoneFilter, filteredResults.length]);
  const totalPages = Math.max(1, Math.ceil((filteredResults?.length || 0) / pageSize));
  const paginatedResults = useMemo(() => {
    if (!filteredResults) return [];
    const start = (currentPage - 1) * pageSize;
    return filteredResults.slice(start, start + pageSize);
  }, [filteredResults, currentPage, pageSize]);

  const getMatchId = useCallback((result, isUnmatched = false) => {
    if (isUnmatched) return `match-unmatched-${result.id}`;
    const matchKey = result?.Matches?.[0] ? Object.keys(result.Matches[0])[0] : null;
    const match = matchKey ? result.Matches[0][matchKey] : null;
    const fallbackId = match?.['System Detected']?.doc1?.ids?.[0]
      || match?.['System Detected']?.doc2?.ids?.[0]
      || matchKey;
    return `match-${result.id || fallbackId}`;
  }, []);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (container.scrollTop !== scrollPositionRef.current) {
      container.scrollTop = scrollPositionRef.current;
    }
  }, [data, currentPage, filteredResults.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      scrollPositionRef.current = container.scrollTop;
    };
    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [currentPage, filteredResults.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let rafId = null;
    const scheduleRestore = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (container.scrollTop !== scrollPositionRef.current) {
          container.scrollTop = scrollPositionRef.current;
        }
      });
    };
    const ro = new ResizeObserver(scheduleRestore);
    const mo = new MutationObserver(scheduleRestore);
    ro.observe(container);
    mo.observe(container, { childList: true, subtree: true, characterData: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [currentPage, filteredResults.length]);


  useLayoutEffect(() => {
    const updateOffset = () => {
      const paginationEl = paginationRef.current;
      const height = paginationEl ? paginationEl.getBoundingClientRect().height : 0;
      setPairingsTopOffset(40 + height);
    };
    updateOffset();
    window.addEventListener('resize', updateOffset);
    return () => window.removeEventListener('resize', updateOffset);
  }, []);

  const PaginationControls = ({ location = 'top' }) => {
    const isSticky = location === 'sticky';
    const containerStyle = isSticky ? {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 14px',
      position: 'fixed',
      bottom: '12px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1200,
      background: 'rgba(255,255,255,0.98)',
      boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
      borderRadius: 10,
      minWidth: 420,
    } : { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', marginTop: '8px' };

    const btnStyle = {
      padding: '6px 14px',
      backgroundColor: '#2B9EA3',
      color: '#fff',
      border: 'none',
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 700,
      fontSize: '0.95rem',
      boxShadow: '0 4px 10px rgba(0,0,0,0.12)'
    };

    const disabledBtnStyle = { ...btnStyle, opacity: 0.45, cursor: 'not-allowed' };

    return (
      <div style={containerStyle} data-pagination-location={location}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={currentPage === 1 ? disabledBtnStyle : btnStyle}
            aria-label="Previous page"
          >
            Prev
          </button>
          
          <div style={{ fontSize: '0.95rem', color: '#426767', fontWeight: 700, margin: '0px 4px' }}>
            Page {currentPage} of {totalPages}
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={currentPage === totalPages ? disabledBtnStyle : btnStyle}
            aria-label="Next page"
          >
            Next
          </button>
        </div>


      </div>
    );
  };

  const handleToggleAllExport = useCallback(async () => {
    if (!data?.results) return;
    setLoadingExport(true);
    const newValue = !selectAllForExport;
    const updatedResults = data.results.map(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      result.Matches[0][matchKey]["Mark For Export"] = newValue;
      return result;
    });
    setData(prev => prev ? { ...prev, results: updatedResults } : prev);
    setSelectAllForExport(newValue);
    await saveResults(data.resultsID, updatedResults);
    setLoadingExport(false);
  }, [data, selectAllForExport, saveResults]);
  
  const handleToggleAllExpand = useCallback(async () => {
    if (!data?.results) return;

    setLoadingExpand(true);
    const newValue = !selectAllExpanded;

    const updatedResults = data.results.map(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      result.Matches[0][matchKey].expanded = newValue;
      return result;
    });

    setData(prev => prev ? { ...prev, results: updatedResults } : prev);
    setSelectAllExpanded(newValue);

    await saveResults(data.resultsID, updatedResults);
    setLoadingExpand(false);
  }, [data, selectAllExpanded, saveResults]);

  const onSaveMatchUpdateCallback = useCallback((resultId, updatedSystem) => {
    saveMatchResults(resultId, updatedSystem, setData, saveResults);
  }, [setData, saveResults]);

  const onSaveMatchNotesCallback = useCallback((resultId, newNotes) => {
    saveMatchNotes(resultId, newNotes, setData, saveResults);
  }, [setData, saveResults]);

  const onSaveMarkForExportCallback = useCallback((resultId, val) => {
    saveMarkForExport(resultId, val, setData, saveResults);
  }, [setData, saveResults]);

  const onSaveLockClickCallback = useCallback((resultId, lineItemId) => {
    saveLock(resultId, lineItemId, setData, comparisonID);
  }, [setData, comparisonID]);

  const onRevertMatchCallback = useCallback((resultId) => {
    revertMatch(resultId, setData, comparisonID);
  }, [setData, comparisonID]);

  const toggleDoc = useCallback((docKey) => {
    setVisibleDocs(prev => ({ ...prev, [docKey]: !prev[docKey] }));
  }, []);

  const setCleanParsedEstData = useCallback((updated) => {
    setData(prev => prev ? { ...prev, cleanParsedEstData: updated } : prev);
  }, []);

  const setCleanParsedInsData = useCallback((updated) => {
    setData(prev => prev ? { ...prev, cleanParsedInsData: updated } : prev);
  }, []);

  const MemoCollapsed = useMemo(() => React.memo(CollapsedMatchBlock), []);
  const MemoExpanded = useMemo(() => React.memo(ExpandedMatchBlock), []);

  const MatchItem = ({ result, isUnmatched = false, matchId }) => {
    const ref = useRef();
    const matchKey = isUnmatched
      ? null
      : Object.keys(result.Matches[0])[0];
    const match = isUnmatched
      ? result.match
      : result.Matches[0][matchKey];
    return (
      <div
        ref={ref}
        id={matchId}
        data-match-id={matchId}
        className={isUnmatched ? 'match-collapse unmatched-block' : 'match-collapse'}
      >
        {match?.expanded ? (
          <MemoExpanded
            match={match}
            cleanParsedEstData={data.cleanParsedEstData}
            setCleanParsedEstData={setCleanParsedEstData}
            cleanParsedInsData={data.cleanParsedInsData}
            setCleanParsedInsData={setCleanParsedInsData}
            headersOne={data.headersOne}
            headersTwo={data.headersTwo}
            docname1={data.doc1_name}
            docname2={data.doc2_name}
            claimId={data.compID}
            visibleDocs={visibleDocs}
            toggleDoc={toggleDoc}
            onToggle={() => {
              toggleExpanded(result.id, setData, saveResults);
            }}
            onSaveMatchUpdate={(updatedSystem) => {
              onSaveMatchUpdateCallback(result.id, updatedSystem);
            }}
            onSaveMatchNotes={(newNotes) => {
              onSaveMatchNotesCallback(result.id, newNotes);
            }}
            onSaveMarkForExport={(val) => {
              onSaveMarkForExportCallback(result.id, val);
            }}
            onSaveLockClick={(lineItemId) => {
              onSaveLockClickCallback(result.id, lineItemId);
            }}
            onRevertMatch={() => {
              onRevertMatchCallback(result.id);
            }}
            resultsID={data.resultsID}
            lineItemUsageMap={lineItemUsageMap}
          />
        ) : (
          <MemoCollapsed
            match={match}
            cleanParsedEstData={data.cleanParsedEstData}
            setCleanParsedEstData={setCleanParsedEstData}
            cleanParsedInsData={data.cleanParsedInsData}
            setCleanParsedInsData={setCleanParsedInsData}
            headersOne={data.headersOne}
            headersTwo={data.headersTwo}
            docname1={data.doc1_name}
            docname2={data.doc2_name}
            claimId={data.compID}
            userId={data.claimData?.user_id}
            filename1={data.files?.[0]}
            filename2={data.files?.[1]}
            visibleDocs={visibleDocs}
            toggleDoc={toggleDoc}
            onToggle={() => {
              toggleExpanded(result.id, setData, saveResults);
            }}
            lineItemUsageMap={lineItemUsageMap}
            doc2NoMatch={isUnmatched}
          />
        )}
      </div>
    );
  };

  const handlePDFExport = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
  
    try {
      if (signedUrl) {
        await fetch(`${process.env.REACT_APP_DELETE_PDF}/${userData._id}/${comparisonID}.pdf`, {
          method: 'DELETE',
        });
        setSignedUrl(null);
      }
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.width = '1920px';
      iframe.style.height = '1080px';
      iframe.style.visibility = 'hidden';
      iframe.src = `${window.location.origin}/restructuredResultspdf/${comparisonID}`;
      document.body.appendChild(iframe);
      iframe.onload = async () => {
        try {
          const waitForContent = () =>
            new Promise((resolve, reject) => {
              let attempts = 0;
              const maxAttempts = 20;
              const check = () => {
                const el = iframe.contentDocument.querySelector('.comparison-block');
                if (el) return resolve();
                if (++attempts > maxAttempts)
                  return reject(new Error('Timeout waiting for React to render'));
                setTimeout(check, 300);
              };
              check();
            });
          await waitForContent();
          const cloned = iframe.contentDocument.body.cloneNode(true);
          cloned.querySelectorAll('[data-pdf-ignore]').forEach(el => el.remove());
          const html = cloned.innerHTML;
          alert('Your PDF is now being generated. You can safely leave this page and return later to view/download it.');
          fetch(`${process.env.REACT_APP_GENERATE_PDF}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              html,
              comparisonID,
              userEmail: userData.email,
              userId: userData._id,
              userFName: userData.name,
              userLName: userData.lastName
            }),
          }).catch(err => console.error('PDF generation queue error:', err));
          const res = await fetch(`${process.env.REACT_APP_GET_PDFS}/${userData._id}/${comparisonID}.pdf`);
          if (res.ok) {
            const awsdata = await res.json();
            if (awsdata?.url) setSignedUrl(awsdata.url);
          }
        } catch (err) {
          console.error('PDF generation error:', err);
          alert('Failed to generate PDF.');
        } finally {
          iframe.remove();
        }
      };
    } catch (err) {
      console.error('Error deleting existing PDF:', err);
      alert('Failed to delete existing PDF before regenerating.');
    }
  }, [isLoading, signedUrl, comparisonID, userData._id, userData.email]);

  const startAprilFoolsSequence = useCallback(() => {
    setAprilFoolsProgress(0);
    setAprilFoolsOutput([]);
    setAprilFoolsStage('deleting');
  }, []);

  if (shouldRunAprilFools && aprilFoolsStage !== 'done') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 999999,
          background: aprilFoolsStage === 'fetching' ? '#ffffff' : '#0b1117',
          color: aprilFoolsStage === 'fetching' ? '#334' : '#d2ffde',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          padding: '24px',
          fontFamily: "'Courier New', monospace",
        }}
      >
        {aprilFoolsStage === 'prompt' && (
          <div
            style={{
              width: 'min(680px, 92vw)',
              background: '#111a23',
              border: '1px solid #2b3f53',
              borderRadius: 10,
              boxShadow: '0 16px 60px rgba(0,0,0,0.45)',
              padding: '24px 24px 20px',
            }}
          >
            <div style={{ fontSize: 14, color: '#9ab3c8', marginBottom: 8 }}>LEVLR SYSTEM ALERT</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#ffb3b3', marginBottom: 10 }}>
              Delete Production Claims Database?
            </div>
            <div style={{ color: '#c6d6e3', lineHeight: 1.4, marginBottom: 18 }}>
              This action is permanent and cannot be undone. All active comparisons, line items, and claim history will be destroyed.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={startAprilFoolsSequence}
                style={{
                  border: '1px solid #5a738a',
                  background: '#1c2936',
                  color: '#e6f0f8',
                  padding: '9px 16px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
              >
                No, Keep Data
              </button>
              <button
                onClick={startAprilFoolsSequence}
                style={{
                  border: '1px solid #8f2424',
                  background: '#cc3d3d',
                  color: '#fff',
                  padding: '9px 16px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 800,
                }}
              >
                Yes, Delete DB
              </button>
            </div>
          </div>
        )}

        {aprilFoolsStage === 'deleting' && (
          <div style={{ width: 'min(860px, 94vw)' }}>
            <div style={{ fontSize: 18, color: '#ffb3b3', marginBottom: 10, fontWeight: 700 }}>
              Deleting database...
            </div>
            <div
              style={{
                width: '100%',
                height: 14,
                borderRadius: 10,
                border: '1px solid #33506b',
                overflow: 'hidden',
                marginBottom: 14,
                background: '#0d1f2f',
              }}
            >
              <div
                style={{
                  width: `${aprilFoolsProgress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #d64545, #ff9f43)',
                  transition: 'width 0.2s ease-out',
                }}
              />
            </div>
            <div style={{ fontSize: 13, color: '#ffcc99', marginBottom: 12 }}>
              Progress: {aprilFoolsProgress}% complete
            </div>
            <div
              ref={aprilFoolsOutputRef}
              style={{
                height: 'min(44vh, 340px)',
                overflowY: 'auto',
                border: '1px solid #27445f',
                background: '#081018',
                borderRadius: 8,
                padding: '12px 14px',
                lineHeight: 1.45,
                fontSize: 13,
              }}
            >
              {aprilFoolsOutput.map((line, idx) => (
                <div key={`${line}-${idx}`} style={{ color: idx % 3 === 0 ? '#ff9f9f' : '#9ad7ff' }}>
                  {line}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, color: '#7e8b98', fontSize: 12 }}>
              Warning: Do not close this window.
            </div>
          </div>
        )}

        {aprilFoolsStage === 'fetching' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
              trying to fetch data
            </div>
            <div style={{ color: '#6f7b85', fontSize: 14 }}>
              reconnecting to claim services...
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="outer-container restructured-results">
      <MainWrapper />
      <div className="right-container">
        <div className="comparison-results-container">
          <Filters selectedFilter={designationFilter} onChange={setDesignationFilter} />
          <ZoneFilter zones={doc1Zones} selectedZones={zoneFilter} onChange={setZoneFilter} />
          <div style={{ position: 'fixed', top: '11%', right: '28%', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={selectAllForExport} onChange={handleToggleAllExport} id="select-all-export" disabled={loadingExport} />
            <label htmlFor="select-all-export" style={{ fontWeight: '600', color: '#42999d' }}>
              {loadingExport ? "Updating..." : "Mark All for Export"}
            </label>
            <button
              onClick={handleToggleAllExpand}
              disabled={loadingExpand}
              style={{
                backgroundColor: '#489EA3',
                color: 'white',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '0px',
                padding: '6px 12px',
                cursor: loadingExpand ? 'not-allowed' : 'pointer',
                opacity: loadingExpand ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                zIndex: 1000,
                marginLeft: '32px',
                boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
              }}
            >
              {loadingExpand ? (
                <span>Working...</span>
              ) : (
                <>
                  <img 
                    src={selectAllExpanded ? collapseIcon : expandIcon} 
                    alt={selectAllExpanded ? "COLLAPSE ALL" : "EXPAND ALL"} 
                    style={{ width: 16, height: 16 }} 
                  />
                  {selectAllExpanded ? "COLLAPSE ALL" : "EXPAND ALL"}
                </>
              )}
            </button>
          </div>
          <div
            style={{
              position: 'absolute',
              top: '42px',
              right: '6px',
              zIndex: 1000,
              display: 'flex',
              height: '36px',
              gap: '6px',
              overflow: 'hidden',
              boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
            }}
          >
            <button
              onClick={handlePDFExport}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0 10px',
                backgroundColor: isLoading ? '#5b5d5d' : '#49a7ab',
                color: '#fff',
                border: 'none',
                fontWeight: '600',
                textTransform: 'uppercase',
                fontFamily: 'inherit',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                overflow: 'hidden',
              }}
            >
              <img
                src={PDFExport}
                alt="PDF"
                style={{ width: '24px', height: 'auto', objectFit: 'contain', flexShrink: 0 }}
              />
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                {isLoading ? 'Generating PDF' : signedUrl ? 'Regenerate PDF' : 'Generate PDF'}
                {isLoading && (
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '3px solid #fff',
                      borderTop: '3px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      flexShrink: 0,
                    }}
                  />
                )}
              </span>
            </button>
            <a
              href={signedUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { if (!signedUrl) e.preventDefault(); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0 10px',
                backgroundColor: '#49a7ab',
                color: '#fff',
                fontWeight: '600',
                textTransform: 'uppercase',
                textDecoration: 'none',
                fontFamily: 'inherit',
                cursor: signedUrl ? 'pointer' : 'not-allowed',
                overflow: 'hidden',
                opacity: signedUrl ? 1 : 0.3,
                justifyContent: 'center',
                border: 'none',
              }}
            >
              <img
                src={pdfIcon}
                alt="PDF Icon"
                style={{ width: '24px', height: 'auto', objectFit: 'contain', flexShrink: 0 }}
              />
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                DOWNLOAD PDF
              </span>
            </a>
            <style>
              {`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
          <NukeButton onClick={() => revertAllMatches(setData, comparisonID)} />
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            {visibleDocs.doc1 && data?.files?.[0] && (
              <PDFViewer userId={data.claimData.user_id} filename={data.files[0]} onClose={() => toggleDoc('doc1')} docType="doc1" />
            )}
            {visibleDocs.doc2 && data?.files?.[1] && (
              <PDFViewer userId={data.claimData.user_id} filename={data.files[1]} onClose={() => toggleDoc('doc2')} docType="doc2" />
            )}
          </div>
          {data?.doc1_name && data?.doc2_name && (
            <div
              className="column-headers"
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                marginBottom: 0,
                padding: '0 16px',
                position: 'absolute',
                width: '100%',
                boxSizing: 'border-box',
                top: '60px',
                borderBottom: '1px solid #42999d',
              }}
            >
              {[0, 1].map(i => {
                const name = i === 0 ? data.doc1_name : data.doc2_name;
                const price = i === 0 ? data?.estPriceList : data?.insPriceList;
                const truncatedName = name.length > 30 ? name.slice(0, 27) + '...' : name;
                return (
                  <div
                    key={i}
                    style={{
                      textAlign: 'left',
                      width: '50%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      paddingLeft: i === 0 ? '0 6px' : '12px',
                      borderRight: i === 0 ? '1px solid #ccc' : '0',
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        color: '#42999d',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: '600',
                        fontSize: '1.1rem',
                      }}
                      title={name}
                    >
                      {truncatedName}
                    </h4>
                    {price && (
                      <i
                        style={{
                          margin: 0,
                          color: '#839299',
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontStyle: 'normal',
                        }}
                      >
                        Price List: {price}
                        {data?.estPriceList && data?.insPriceList && (
                          <img
                            src={data.estPriceList !== data.insPriceList ? yieldIcon : GoIcon}
                            alt={data.estPriceList !== data.insPriceList ? 'yield icon' : 'go icon'}
                            style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                          />
                        )}
                      </i>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <style>
            {`
              .match-placeholder { width: 100%; min-height: 110px; background: transparent; }
            `}
          </style>
          <div ref={paginationRef} style={{ position: 'fixed', zIndex: 2000, top: '218px' }}>
            <PaginationControls location="top" />
          </div>
          <div
            id="pairings"
            className="pairings-container"
            style={{ marginTop: '64px', overflowAnchor: 'none' }}
            ref={scrollContainerRef}
          >
            <div className={loadingStatus}></div>
              {paginatedResults.map(result => {
                const matchId = getMatchId(result);
                return (
                  <MatchItem key={matchId} result={result} matchId={matchId} />
                );
              })}
            {data?.claimData?.doc2_name && (
              <h4
                style={{
                  fontSize: '24px',
                  color: '#42999d',
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  margin: '32px auto 64px auto'
                }}
              >
                No Matches From: <i style={{ color: '#839299' }}>{data.claimData.doc2_name}</i>
              </h4>
            )}
            {unusedInsLineItems?.map(item => {
              const id = Object.keys(item)[0];
              const mockMatch = {
                expanded: false,
                'System Detected': { doc1: { ids: [] }, doc2: { ids: [id] }, Designation: 'No-Match' },
                'User Modified': { doc1: { ids: [] }, doc2: { ids: [id] }, Designation: 'No-Match', Notes: '', MarkForExport: false }
              };
              const matchId = getMatchId({ id }, true);
              return (
                <MatchItem key={matchId} matchId={matchId} result={{ id, match: mockMatch }} isUnmatched={true} />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestructuredResults;
